import RenderSequenceTimeline from "page/Crm/Children/SequenceManager/Components/RenderSequenceTimeline"
import { useState } from "react";
import { Calendar, Clock, Mail, Play, Pause, RotateCcw, Globe, Search, Database, ChevronDown, ChevronUp } from 'lucide-react';
const perplexityInsights: any = {
    "ByteTech Solutions Ltd": {
        lastChecked: "2024-01-09",
        updates: [
            {
                date: "2024-01-05",
                source: "TechCrunch",
                insight: "Secured £5M Series A funding for UK expansion",
                sentiment: "positive",
                relevance: "high"
            },
            {
                date: "2024-01-02",
                source: "LinkedIn",
                insight: "Hired new Head of IP Operations in London office",
                sentiment: "positive",
                relevance: "high"
            }
        ]
    },
    "GreenPower Innovations": {
        lastChecked: "2024-01-10",
        updates: [
            {
                date: "2024-01-08",
                source: "Reuters",
                insight: "Announced partnership with UK energy provider",
                sentiment: "positive",
                relevance: "medium"
            }
        ]
    },
    "HealthTech Shanghai Co": {
        lastChecked: "2024-01-10",
        updates: [
            {
                date: "2024-01-07",
                source: "MedTech News",
                insight: "Filed for UK medical device certification",
                sentiment: "positive",
                relevance: "high"
            },
            {
                date: "2024-01-03",
                source: "Companies House",
                insight: "Registered new UK subsidiary",
                sentiment: "positive",
                relevance: "high"
            }
        ]
    }
};

const chineseApplicantData: any = {
    companies: [
        {
            name: "ByteTech Solutions Ltd",
            location: "Shenzhen/London",
            industry: "AI & Machine Learning",
            sonarResults: "High activity in UK trademark filings",
            domainResults: "bytetech-uk.com, bytetechglobal.com",
            lastApproach: "2023-12-15",
            status: "In sequence 2/5"
        },
        {
            name: "GreenPower Innovations",
            location: "Shanghai/Manchester",
            industry: "Renewable Energy",
            sonarResults: "Recent patent applications in UK",
            domainResults: "greenpowerinnovations.co.uk",
            lastApproach: "2024-01-02",
            status: "Sequence complete"
        },
        {
            name: "HealthTech Shanghai Co",
            location: "Shanghai/Birmingham",
            industry: "Medical Devices",
            sonarResults: "Multiple UK distributor searches",
            domainResults: "healthtech-global.com",
            lastApproach: "2024-01-10",
            status: "New lead"
        }
    ]
};

// Email sequence templates
const sequenceTemplates: any = {
    chineseMarketEntry: {
        name: "Chinese Market UK Entry",
        steps: [
            {
                day: 1,
                subject: "UK IP Protection for Chinese Businesses",
                template: "initial_approach",
                waitDays: 3
            },
            {
                day: 4,
                subject: "Protecting Your Innovation in the UK Market",
                template: "value_proposition",
                waitDays: 5
            },
            {
                day: 9,
                subject: "Success Stories: Chinese Companies in UK",
                template: "social_proof",
                waitDays: 4
            },
            {
                day: 13,
                subject: "Special UK-China IP Protection Package",
                template: "offer",
                waitDays: 7
            },
            {
                day: 20,
                subject: "Final Opportunity: UK Market Protection",
                template: "final_call",
                waitDays: null
            }
        ]
    }
};

const Main = () => {
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [activeSequence, setActiveSequence] = useState(null);
    const [expandedCompanies, setExpandedCompanies] = useState(new Set());
    const [activeTab, setActiveTab] = useState("chinese_market");

    const toggleCompanyDetails = (companyName: any) => {
        const newExpanded = new Set(expandedCompanies);
        if (newExpanded.has(companyName)) {
            newExpanded.delete(companyName);
        } else {
            newExpanded.add(companyName);
        }
        setExpandedCompanies(newExpanded);
    };
    return (
        <div className="space-y-6">
            {/* Active Sequences Overview */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className='font-bold'>Active Sequences</div>
                    <div>Currently running email sequences</div>
                </div>
                <div>
                    <div className="space-y-4 mt-2">
                        {chineseApplicantData.companies.map((company: any) => (
                            <div key={company.name} className="p-4 border rounded-lg">
                                <div
                                    className="flex justify-between items-start mb-3 cursor-pointer"
                                    onClick={() => toggleCompanyDetails(company.name)}
                                >
                                    <div>
                                        <h3 className="font-medium">{company.name}</h3>
                                        <p className="text-sm text-gray-500">{company.location}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className={`text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm ${company.status.includes("sequence") ? "bg-[#d1fae5] text-[#065f46]" : "bg-blue-100 text-blue-800"}`}>
                                            {company.status}
                                        </div>
                                        {expandedCompanies.has(company.name) ?
                                            <ChevronUp className="w-4 h-4" /> :
                                            <ChevronDown className="w-4 h-4" />
                                        }
                                    </div>
                                </div>

                                {expandedCompanies.has(company.name) && (
                                    <div className="mt-4 space-y-2 pl-4 border-l">
                                        <div className="flex items-center gap-2">
                                            <Search className="w-4 h-4 text-gray-400" />
                                            <span className="text-sm">{company.sonarResults}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Globe className="w-4 h-4 text-gray-400" />
                                            <span className="text-sm">{company.domainResults}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Calendar className="w-4 h-4 text-gray-400" />
                                            <span className="text-sm">Last Approach: {company.lastApproach}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Sequence Templates */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className='font-bold'>Sequence Templates</div>
                    <div>Predefined email sequences for different scenarios</div>
                </div>
                <div className='mt-2'>
                    <div className="sm:hidden">
                        <label htmlFor="tabs" className="sr-only">Select your country</label>
                        <select className="focus:outline-none w-full rounded-2xl py-2 mb-3 px-4 border-4 border-main text-primary"
                            onChange={(e) => setActiveTab(e.target.value)}
                        >
                            <option value="chinese_market">Chinese Market</option>
                            <option value="renewals">Renewals</option>
                            <option value="startups">Startups</option>
                        </select>
                    </div>
                    <ul className="hidden text-sm font-medium text-center text-[#6B7280] rounded-lg shadow sm:flex">
                        <li className="w-full focus-within:z-10" onClick={() => setActiveTab('chinese_market')}>
                            <a href="#" className={`inline-block font-bold w-full p-4 text-[#111827] border-r border-[#E5E7EB] rounded-s-lg focus:outline-none ${activeTab === 'chinese_market' ? 'active bg-[#F3F4F6] ' : 'bg-white'}`}>Chinese Market</a>
                        </li>
                        <li className="w-full focus-within:z-10" onClick={() => setActiveTab('renewals')}>
                            <a href="#" className={`inline-block font-bold w-full p-4 text-[#111827] border-r border-[#E5E7EB] focus:outline-none ${activeTab === 'renewals' ? 'active bg-[#F3F4F6]' : 'bg-white'}`}>Renewals</a>
                        </li>
                        <li className="w-full focus-within:z-10" onClick={() => setActiveTab('startups')}>
                            <a href="#" className={`inline-block font-bold w-full p-4 text-[#111827] border-r border-[#E5E7EB] focus:outline-none ${activeTab === 'startups' ? 'active bg-[#F3F4F6] ' : 'bg-white'}`}>Startups</a>
                        </li>
                    </ul>
                    <div className='mt-2'>
                        {activeTab === 'chinese_market' && <>
                            {RenderSequenceTimeline(sequenceTemplates.chineseMarketEntry)}
                        </>}
                    </div>

                </div>
            </div>

            {/* Sequence Analytics */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className='font-bold'>Sequence Analytics</div>
                    <div>Performance metrics for your email sequences</div>
                </div>
                <div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-4 border rounded-lg">
                            <div className="text-sm text-gray-500">Open Rate</div>
                            <div className="text-2xl font-bold">32.5%</div>
                        </div>
                        <div className="p-4 border rounded-lg">
                            <div className="text-sm text-gray-500">Response Rate</div>
                            <div className="text-2xl font-bold">12.8%</div>
                        </div>
                        <div className="p-4 border rounded-lg">
                            <div className="text-sm text-gray-500">Meeting Booked</div>
                            <div className="text-2xl font-bold">5.2%</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sequence Controls */}
            <div className="flex gap-4">
                <button className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">
                    <Play className="w-4 h-4" />
                    Start New Sequence
                </button>
                <button className="flex items-center gap-2 px-4 py-2 rounded-lg border border-green text-green">
                    <Pause className="w-4 h-4" />
                    Pause All
                </button>
                <button className="flex items-center gap-2 px-4 py-2 rounded-lg border border-green text-green">
                    <RotateCcw className="w-4 h-4" />
                    Reset
                </button>
            </div>
        </div>
    )
}
export default Main